import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, Trash2, Loader2, ShieldAlert, Undo2 } from "lucide-react";
import { format } from "date-fns";

export function PrivacyDataCard() {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deletionScheduledAt, setDeletionScheduledAt] = useState<string | null>(null);

  const loadStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("deletion_scheduled_at")
      .eq("user_id", user.id)
      .single();
    setDeletionScheduledAt(data?.deletion_scheduled_at ?? null);
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not signed in");

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.functions.supabase.co/export-my-data`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `her-season-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: "Export ready", description: "Your data has been downloaded." });
    } catch (err: any) {
      toast({ title: "Export failed", description: err.message, variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const handleScheduleDeletion = async () => {
    setScheduling(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const scheduledFor = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from("profiles")
        .update({ deletion_scheduled_at: scheduledFor })
        .eq("user_id", user.id);
      if (error) throw error;
      setDeletionScheduledAt(scheduledFor);
      toast({
        title: "Account scheduled for deletion",
        description: "Your data will be permanently erased in 30 days. You can cancel any time before then.",
      });
    } catch (err: any) {
      toast({ title: "Could not schedule deletion", description: err.message, variant: "destructive" });
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancelling(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const { error } = await supabase
        .from("profiles")
        .update({ deletion_scheduled_at: null })
        .eq("user_id", user.id);
      if (error) throw error;
      setDeletionScheduledAt(null);
      toast({ title: "Deletion cancelled", description: "Your account will not be deleted." });
    } catch (err: any) {
      toast({ title: "Could not cancel deletion", description: err.message, variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="mt-6 border-destructive/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-destructive" />
          Privacy & Your Data
        </CardTitle>
        <CardDescription>
          Download a full copy of your data, or permanently delete your account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border bg-muted/30">
          <div>
            <div className="font-medium">Download my data</div>
            <div className="text-sm text-muted-foreground">
              Get a JSON file with everything we hold about you.
            </div>
          </div>
          <Button variant="outline" onClick={handleExport} disabled={exporting} className="gap-2">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? "Preparing…" : "Download"}
          </Button>
        </div>

        {deletionScheduledAt ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-destructive/40 bg-destructive/5">
            <div>
              <div className="font-medium text-destructive">Account scheduled for deletion</div>
              <div className="text-sm text-muted-foreground">
                Your data will be permanently erased on{" "}
                <strong>{format(new Date(deletionScheduledAt), "PPP")}</strong>.
              </div>
            </div>
            <Button variant="outline" onClick={handleCancelDeletion} disabled={cancelling} className="gap-2">
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
              Cancel deletion
            </Button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border">
            <div>
              <div className="font-medium">Delete my account</div>
              <div className="text-sm text-muted-foreground">
                Starts a 30-day grace period. You can undo this any time before it completes.
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={scheduling} className="gap-2">
                  <Trash2 className="h-4 w-4" />
                  Delete account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Your account will be deactivated immediately and all of your personal data
                    (tracker entries, notes, community posts, and profile) will be permanently
                    erased 30 days from now. You can cancel any time before then by signing back in.
                    We recommend downloading your data first.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep my account</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleScheduleDeletion}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Yes, delete in 30 days
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
